import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FileText, ArrowLeft, Download, CheckCircle, Truck, Package, Building2, MapPin, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../lib/api';
import RetailerLayout from '../components/RetailerLayout';
import { getStatusConfig, fmtPrice, fmtDateLong } from '../lib/orderStatus';

const PurchaseOrderDetailPage = () => {
  const { id } = useParams();
  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/purchase-orders/${id}`).then(r => setPo(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  const handleDownload = async () => {
    try {
      const res = await api.get(`/purchase-orders/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `bon_de_commande_${po?.order_number || 'LP'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { /* silent */ }
  };

  if (loading) return (
    <RetailerLayout>
      <div className="flex items-center justify-center py-16 gap-2 text-sm text-slate-400">
        <div className="animate-spin h-5 w-5 border-2 border-slate-400 border-t-transparent rounded-full" />
        Chargement...
      </div>
    </RetailerLayout>
  );

  if (!po) return (
    <RetailerLayout>
      <div className="text-center py-16">
        <Package className="h-12 w-12 text-slate-200 mx-auto mb-3" />
        <p className="text-sm text-slate-400">Commande introuvable</p>
        <Link to="/dashboard/profil?tab=orders"><Button variant="outline" className="mt-4">Retour</Button></Link>
      </div>
    </RetailerLayout>
  );

  const st = getStatusConfig(po.status);
  const Icon = st.icon;
  const partner = po.snapshot_partner || {};
  const userSnap = po.snapshot_user || {};
  const origTotals = po.snapshot_totals || {};
  const confirmedTotals = po.confirmed_totals || null;
  const displayTotals = confirmedTotals || origTotals;
  const items = po.snapshot_items || [];
  const isModified = po.status === 'confirmed_modified';
  const isCancelled = po.status === 'cancelled';

  return (
    <RetailerLayout>
      <div className="max-w-4xl mx-auto space-y-6 pb-8 px-4" data-testid="po-detail-page">
        {/* BACK + HEADER */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Link to="/dashboard/profil?tab=orders" className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
              <ArrowLeft className="h-5 w-5 text-slate-500" />
            </Link>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-bold text-slate-900" data-testid="po-detail-number">{po.order_number}</h1>
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${st.color}`} data-testid="po-detail-status">
                  <Icon className="h-3.5 w-3.5 inline mr-1" />{st.label}
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-0.5">{fmtDateLong(po.submitted_at || po.created_at)}</p>
            </div>
          </div>
          <Button onClick={handleDownload} className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="po-download-btn">
            <Download className="h-4 w-4" /> Télécharger
          </Button>
        </div>

        {/* Modification banner */}
        {isModified && (
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4" data-testid="modification-banner">
            <p className="text-sm font-medium text-blue-800 flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Le distributeur a confirmé cette commande avec des modifications
            </p>
            {po.partner_global_comment && (
              <p className="text-xs text-blue-700 mt-1 ml-6">"{po.partner_global_comment}"</p>
            )}
            <p className="text-xs text-blue-600 mt-2 ml-6">
              Les différences entre votre demande initiale et la confirmation sont indiquées ci-dessous.
            </p>
          </div>
        )}

        {isCancelled && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4">
            <p className="text-sm font-medium text-red-800">Cette commande a été annulée par le distributeur.</p>
          </div>
        )}

        {/* PARTNER + MAGASIN */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
              <Building2 className="h-4 w-4" /> Distributeur
            </div>
            <p className="text-base font-semibold text-slate-900">{partner.nom}</p>
            {partner.contact_email && <p className="text-xs text-slate-500">{partner.contact_email}</p>}
            {partner.process_commande && <p className="text-xs text-slate-400 mt-1">{partner.process_commande}</p>}
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
              <MapPin className="h-4 w-4" /> Magasin
            </div>
            <p className="text-base font-semibold text-slate-900">{userSnap.nom_magasin || 'Mon magasin'}</p>
            <p className="text-xs text-slate-500">{[userSnap.adresse, userSnap.code_postal, userSnap.ville].filter(Boolean).join(', ') || '—'}</p>
            {userSnap.email && <p className="text-xs text-slate-400">{userSnap.email}</p>}
          </div>
        </div>

        {/* ITEMS TABLE */}
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
            <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
              <Package className="h-4 w-4" /> Produits commandés ({items.length})
            </h2>
          </div>

          {/* Desktop */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm" data-testid="po-items-table">
              <thead>
                <tr className="bg-slate-100 border-b border-slate-200">
                  <th className="text-left text-[10px] font-bold text-slate-500 uppercase px-4 py-2.5">Produit</th>
                  <th className="text-center text-[10px] font-bold text-slate-500 uppercase px-3 py-2.5">EAN</th>
                  {isModified && <th className="text-center text-[10px] font-bold text-slate-500 uppercase px-3 py-2.5">Demandé</th>}
                  <th className="text-center text-[10px] font-bold text-slate-500 uppercase px-3 py-2.5">{isModified ? 'Confirmé' : 'Qté'}</th>
                  <th className="text-center text-[10px] font-bold text-slate-500 uppercase px-3 py-2.5">Gratuit</th>
                  <th className="text-right text-[10px] font-bold text-slate-500 uppercase px-3 py-2.5">Prix unit.</th>
                  <th className="text-right text-[10px] font-bold text-slate-500 uppercase px-4 py-2.5">Total</th>
                  {isModified && <th className="text-left text-[10px] font-bold text-slate-500 uppercase px-3 py-2.5">Note</th>}
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => {
                  const isRemoved = it.item_status === 'removed';
                  const isItemModified = it.item_status === 'modified';
                  const displayTotal = isModified ? (it.confirmed_total_price_paid ?? it.total_price_paid) : it.total_price_paid;
                  const displayQty = isModified ? (it.confirmed_quantity ?? it.quantity) : it.quantity;

                  return (
                    <tr key={idx} className={`border-b border-slate-100 hover:bg-slate-50/50 ${isRemoved ? 'opacity-50 bg-red-50/30' : isItemModified ? 'bg-blue-50/30' : ''}`} data-testid={`po-item-row-${idx}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {it.product_image ? (
                            <img src={it.product_image} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-8 h-8 bg-slate-100 rounded flex items-center justify-center flex-shrink-0"><Package className="h-3 w-3 text-slate-300" /></div>
                          )}
                          <div>
                            <span className={`font-medium text-slate-900 truncate max-w-[200px] block ${isRemoved ? 'line-through' : ''}`}>{it.product_name}</span>
                            {isRemoved && <span className="text-[10px] text-red-600 font-medium">Supprimé par le distributeur</span>}
                            {isItemModified && <span className="text-[10px] text-blue-600 font-medium">Quantité modifiée</span>}
                          </div>
                        </div>
                      </td>
                      <td className="text-center text-xs text-slate-500 font-mono px-3">{it.product_ean || '—'}</td>
                      {isModified && <td className="text-center text-slate-400 px-3 tabular-nums">{it.quantity}</td>}
                      <td className={`text-center font-semibold px-3 tabular-nums ${isRemoved ? 'text-red-600' : isItemModified ? 'text-blue-700' : 'text-slate-900'}`}>
                        {displayQty}
                      </td>
                      <td className="text-center text-emerald-600 px-3 tabular-nums">{it.free_units > 0 ? `+${it.free_units}` : '—'}</td>
                      <td className="text-right text-slate-700 px-3 tabular-nums">{fmtPrice(it.purchase_price)}</td>
                      <td className="text-right font-bold text-slate-900 px-4 tabular-nums">{fmtPrice(displayTotal)}</td>
                      {isModified && (
                        <td className="text-left text-xs text-slate-500 px-3 max-w-[150px]">
                          {it.partner_comment || '—'}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile */}
          <div className="md:hidden p-3 space-y-2">
            {items.map((it, idx) => {
              const isRemoved = it.item_status === 'removed';
              const isItemModified = it.item_status === 'modified';
              const displayQty = isModified ? (it.confirmed_quantity ?? it.quantity) : it.quantity;
              const displayTotal = isModified ? (it.confirmed_total_price_paid ?? it.total_price_paid) : it.total_price_paid;

              return (
                <div key={idx} className={`p-3 rounded-lg border flex flex-col gap-2 ${isRemoved ? 'bg-red-50/50 border-red-200 opacity-60' : isItemModified ? 'bg-blue-50/50 border-blue-200' : 'bg-slate-50 border-slate-100'}`}>
                  <div className="flex items-center gap-3">
                    {it.product_image ? (
                      <img src={it.product_image} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center flex-shrink-0"><Package className="h-4 w-4 text-slate-300" /></div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium text-slate-900 truncate ${isRemoved ? 'line-through' : ''}`}>{it.product_name}</p>
                      {isModified ? (
                        <p className="text-xs text-slate-500">
                          Demandé : {it.quantity} / Confirmé : <span className={isRemoved ? 'text-red-600 font-semibold' : isItemModified ? 'text-blue-700 font-semibold' : ''}>{displayQty}</span>
                           @ {fmtPrice(it.purchase_price)}
                        </p>
                      ) : (
                        <p className="text-xs text-slate-500">x{it.quantity} @ {fmtPrice(it.purchase_price)}</p>
                      )}
                      {isRemoved && <p className="text-[10px] text-red-600 font-medium">Supprimé par le distributeur</p>}
                    </div>
                    <p className="text-sm font-bold text-slate-900 tabular-nums">{fmtPrice(displayTotal)}</p>
                  </div>
                  {isModified && it.partner_comment && (
                    <p className="text-[10px] text-slate-500 ml-13 pl-[52px]">Note : {it.partner_comment}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* TOTALS */}
        <div className="rounded-xl border-2 border-indigo-200 bg-indigo-50/30 p-5 space-y-3" data-testid="po-totals">
          {/* Show original totals if modified */}
          {isModified && confirmedTotals && (
            <div className="flex justify-between text-sm">
              <span className="text-slate-400 line-through">Sous-total initial</span>
              <span className="tabular-nums text-slate-400 line-through">{fmtPrice(origTotals.subtotal)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">{isModified ? 'Sous-total confirmé' : 'Sous-total produits'}</span>
            <span className={`font-semibold tabular-nums ${isModified ? 'text-blue-700' : ''}`}>{fmtPrice(displayTotals.subtotal)}</span>
          </div>
          {displayTotals.franco_threshold != null && (
            <div className={`flex items-center justify-between p-2 rounded-lg text-sm ${displayTotals.franco_reached ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
              {displayTotals.franco_reached ? (
                <><span className="flex items-center gap-1 text-emerald-700"><CheckCircle className="h-4 w-4" /> Franco atteint</span><span className="font-semibold text-emerald-700">{fmtPrice(displayTotals.franco_threshold)}</span></>
              ) : (
                <><span className="text-amber-700">Franco non atteint</span><span className="font-semibold text-amber-700">{fmtPrice(displayTotals.franco_threshold)}</span></>
              )}
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="flex items-center gap-1 text-slate-600"><Truck className="h-4 w-4" /> Frais de port</span>
            <span className={`font-semibold tabular-nums ${displayTotals.shipping_cost === 0 ? 'text-emerald-600' : 'text-slate-900'}`}>
              {displayTotals.shipping_cost === 0 ? 'Gratuit' : fmtPrice(displayTotals.shipping_cost)}
            </span>
          </div>
          <div className="flex justify-between pt-2 border-t border-indigo-200">
            <span className="text-lg font-bold text-slate-900">Total HT</span>
            <span className="text-2xl font-black text-indigo-700 tabular-nums" data-testid="po-detail-total">{fmtPrice(displayTotals.total)}</span>
          </div>
          {isModified && confirmedTotals && origTotals.total !== confirmedTotals.total && (
            <p className="text-xs text-blue-600 text-right">
              (Total initial : {fmtPrice(origTotals.total)})
            </p>
          )}
        </div>
      </div>
    </RetailerLayout>
  );
};

export default PurchaseOrderDetailPage;
